import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { photoUrl, INR } from "@/lib/api";
import { BedDouble } from "lucide-react";

// Fix for leaflet default marker icon
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [22.7196, 75.8577];

export default function ListingsMap({ listings = [] }) {
  const validListings = useMemo(() => listings.filter(
    (l) => l.latitude !== null && l.longitude !== null
  ), [listings]);

  const [center, setCenter] = useState(DEFAULT_CENTER);

  useEffect(() => {
    if (validListings.length > 0) {
      setCenter([validListings[0].latitude, validListings[0].longitude]);
    }
  }, [validListings]);

  return (
    <div className="w-full h-full min-h-[450px] rounded-2xl overflow-hidden border border-stone-200 shadow-sm relative z-10">
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom={true}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {validListings.map((listing) => (
          <Marker
            key={listing.id}
            position={[listing.latitude, listing.longitude]}
          >
            <Popup>
              <div className="w-48 p-0.5 bg-white font-sans text-stone-900">
                {listing.photos && listing.photos.length > 0 && (
                  <img
                    src={photoUrl(listing.photos[0])}
                    alt={listing.title}
                    className="w-full h-24 object-cover rounded-lg mb-2"
                  />
                )}
                <div className="font-display font-medium text-xs line-clamp-1 mb-1">
                  {listing.title}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-stone-500 mb-1.5">
                  <span className="bg-stone-100 py-0.5 px-1.5 rounded">{listing.bhk}</span>
                  <span className="bg-stone-100 py-0.5 px-1.5 rounded capitalize">{listing.room_type}</span>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-100">
                  <span className="font-semibold text-[#2A4B3C] text-xs">{INR(listing.rent)}/mo</span>
                  <Link
                    to={`/listing/${listing.id}`}
                    className="text-[10px] bg-[#C84B31] text-white px-2 py-0.5 rounded-full font-medium hover:bg-[#A63A25] transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
        <MapController position={center} />
      </MapContainer>
    </div>
  );
}

function MapController({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.panTo(position);
    }
  }, [position, map]);
  return null;
}
